import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Card } from '@/components/ui/card';
import { toast } from 'sonner';
import { Check, Eraser, Loader2 } from 'lucide-react';
import {
  CONSENT_ITEMS,
  HEALTH_QUESTIONS,
  PHOTO_CONSENT_LABEL,
  TREATMENT_TYPES,
  YES_NO_LABEL,
  emptyAnswers,
  validateAnswers,
  type HealthFormAnswers,
  type PhotoConsent,
  type YesNo,
} from '@/lib/healthForm';

const FN_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/health-form-public`;
const ANON = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
const LOGO_SRC =
  'https://cdn.shopify.com/s/files/1/0977/6965/2535/files/Container2_f5f82266-92cf-4bb7-a432-7dc3063fd646.png?v=1787083209';

const PDF_FONT = 'Arial, "Helvetica Neue", Helvetica, system-ui, sans-serif';

/** True when every sampled pixel of the canvas is (near) white. */
function isBlankCanvas(canvas: HTMLCanvasElement): boolean {
  const ctx = canvas.getContext('2d');
  if (!ctx) return false;
  try {
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    for (let i = 0; i < data.length; i += 16) {
      if (data[i] < 246 || data[i + 1] < 246 || data[i + 2] < 246) return false;
    }
    return true;
  } catch {
    return false;
  }
}

/** Crops trailing white rows so a few overflowing pixels can't spawn an empty page. */
function trimBottomWhitespace(canvas: HTMLCanvasElement): HTMLCanvasElement {
  const ctx = canvas.getContext('2d');
  if (!ctx) return canvas;
  let lastContentRow = canvas.height - 1;
  try {
    const { data } = ctx.getImageData(0, 0, canvas.width, canvas.height);
    outer: for (let y = canvas.height - 1; y >= 0; y--) {
      for (let x = 0; x < canvas.width; x += 2) {
        const i = (y * canvas.width + x) * 4;
        if (data[i] < 246 || data[i + 1] < 246 || data[i + 2] < 246) {
          lastContentRow = y;
          break outer;
        }
      }
    }
  } catch {
    return canvas;
  }
  const padding = Math.round(canvas.width * 0.03);
  const height = Math.min(canvas.height, lastContentRow + 1 + padding);
  if (height >= canvas.height - 2) return canvas;
  const out = document.createElement('canvas');
  out.width = canvas.width;
  out.height = height;
  out.getContext('2d')?.drawImage(canvas, 0, 0, canvas.width, height, 0, 0, canvas.width, height);
  return out;
}


async function callFn(payload: Record<string, unknown>) {
  const resp = await fetch(FN_URL, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', apikey: ANON, Authorization: `Bearer ${ANON}` },
    body: JSON.stringify(payload),
  });
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new Error(data?.error || 'שגיאה בשרת');
  return data;
}

export default function HealthFormPublic() {
  const { token: rawToken = '' } = useParams();
  const token = rawToken === 'new' ? '' : rawToken;
  const openMode = !token;
  const [loading, setLoading] = useState(!openMode);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [alreadyFilled, setAlreadyFilled] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [answers, setAnswers] = useState<HealthFormAnswers>(emptyAnswers);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const sigRef = useRef<SignatureCanvas | null>(null);
  const printRef = useRef<HTMLDivElement | null>(null);
  const [signatureData, setSignatureData] = useState<string | null>(null);

  useEffect(() => {
    if (openMode) return;
    (async () => {
      try {
        const data = await callFn({ action: 'get', token });
        if (data.status === 'filled') setAlreadyFilled(true);
        setAnswers((prev) => ({
          ...prev,
          full_name: data.patient?.name ?? '',
          email: data.patient?.email ?? '',
          phone: data.patient?.phone ?? '',
          birth_date: data.patient?.birthDate ?? '',
        }));
      } catch (e) {
        setLoadError(e instanceof Error ? e.message : 'שגיאה');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, openMode]);

  const set = <K extends keyof HealthFormAnswers>(key: K, value: HealthFormAnswers[K]) =>
    setAnswers((prev) => ({ ...prev, [key]: value }));

  const todayLabel = useMemo(() => new Date().toLocaleDateString('he-IL'), []);

  const buildPdf = async (): Promise<string> => {
    const node = printRef.current;
    if (!node) throw new Error('שגיאה ביצירת המסמך');
    node.style.display = 'block';
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });
    const pageW = pdf.internal.pageSize.getWidth();
    const pageH = pdf.internal.pageSize.getHeight();
    try {
      const sheet = node.querySelector<HTMLElement>('[data-pdf-page]') ?? node;
      const rendered = await html2canvas(sheet, { scale: 2, backgroundColor: '#ffffff', useCORS: true });
      // Trim trailing blank (white) rows so a few overflowing pixels never create an empty page.
      const canvas = trimBottomWhitespace(rendered);
      const fullH = (canvas.height * pageW) / canvas.width;
      // Anything up to ~6% over A4 is squeezed onto a single page instead of spilling over.
      if (fullH <= pageH * 1.06) {
        pdf.addImage(canvas.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, pageW, Math.min(fullH, pageH));
      } else {
        // Overflow safety: slice the rendered sheet into A4 pages.
        const sliceHeightPx = Math.floor((pageH * canvas.width) / pageW);
        for (let offset = 0, page = 0; offset < canvas.height; offset += sliceHeightPx, page++) {
          const h = Math.min(sliceHeightPx, canvas.height - offset);
          const slice = document.createElement('canvas');
          slice.width = canvas.width;
          slice.height = h;
          const ctx = slice.getContext('2d');
          ctx?.drawImage(canvas, 0, offset, canvas.width, h, 0, 0, canvas.width, h);
          if (page > 0 && isBlankCanvas(slice)) continue;
          if (page > 0) pdf.addPage();
          pdf.addImage(slice.toDataURL('image/jpeg', 0.94), 'JPEG', 0, 0, pageW, (h * pageW) / canvas.width);
        }
      }

    } finally {
      node.style.display = 'none';
    }
    return pdf.output('datauristring').split(',')[1];
  };

  const submit = async () => {
    if (submitting) return;
    const sig = sigRef.current;
    const signature = sig && !sig.isEmpty() ? sig.getCanvas().toDataURL('image/png') : null;
    const payload: HealthFormAnswers = { ...answers, signed_at: new Date().toISOString() };
    const result = validateAnswers(payload, !!signature);
    setErrors(result.errors);
    if (!result.ok) {
      toast.error('נא להשלים את השדות המסומנים');
      const first = document.querySelector('[data-invalid="true"]');
      first?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }
    setSubmitting(true);
    setSignatureData(signature);
    try {
      // Let the print template render with the signature before rasterising.
      await new Promise((r) => setTimeout(r, 80));
      const pdf = await buildPdf();
      await callFn(
        openMode
          ? { action: 'openSubmit', answers: payload, signature, pdf }
          : { action: 'submit', token, answers: payload, signature, pdf },
      );
      setSubmitted(true);
      window.scrollTo({ top: 0 });
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'שליחה נכשלה');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center text-sm text-muted-foreground">
        טוען…
      </div>
    );
  }

  if (loadError) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <Card className="max-w-md w-full p-8 text-center space-y-3">
          <h1 className="text-xl font-semibold">הקישור אינו תקין</h1>
          <p className="text-sm text-muted-foreground">{loadError}</p>
        </Card>
      </div>
    );
  }

  if (submitted || alreadyFilled) {
    return (
      <div dir="rtl" className="min-h-screen flex items-center justify-center p-6 bg-neutral-50">
        <Card className="max-w-md w-full p-8 text-center space-y-3">
          <div className="mx-auto h-12 w-12 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
            <Check className="h-6 w-6" />
          </div>
          <h1 className="text-xl font-semibold">
            {submitted ? 'ההצהרה נשלחה בהצלחה' : 'ההצהרה כבר מולאה'}
          </h1>
          <p className="text-sm text-muted-foreground">
            עותק חתום נשלח לכתובת האימייל שלך ולקליניקה. נתראה בטיפול!
          </p>
        </Card>
      </div>
    );
  }

  const invalid = (key: string) => (errors[key] ? { 'data-invalid': 'true' as const } : {});

  const half = Math.ceil(HEALTH_QUESTIONS.length / 2);
  const questionColumns = [HEALTH_QUESTIONS.slice(0, half), HEALTH_QUESTIONS.slice(half)];

  return (
    <div dir="rtl" className="min-h-screen bg-neutral-50 py-8 px-4">
      <div className="mx-auto max-w-2xl space-y-5">
        <header className="text-center space-y-2">
          <img src={LOGO_SRC} alt="RIHAM" className="mx-auto h-12 w-auto" />
          <h1 className="text-2xl font-semibold text-neutral-900">הצהרת בריאות והסכמה לטיפול</h1>
          <p className="text-sm text-muted-foreground">נא למלא את כל השדות ולחתום בתחתית הטופס.</p>
        </header>

        {/* Personal details */}
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">פרטים אישיים</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div {...invalid('full_name')}>
              <Label className="text-xs">שם מלא *</Label>
              <Input value={answers.full_name} onChange={(e) => set('full_name', e.target.value)} />
              {errors.full_name && <p className="text-xs text-red-600 mt-1">{errors.full_name}</p>}
            </div>
            <div {...invalid('id_number')}>
              <Label className="text-xs">מספר זהות *</Label>
              <Input
                inputMode="numeric"
                value={answers.id_number}
                onChange={(e) => set('id_number', e.target.value)}
              />
              {errors.id_number && <p className="text-xs text-red-600 mt-1">{errors.id_number}</p>}
            </div>
            <div {...invalid('birth_date')}>
              <Label className="text-xs">תאריך לידה *</Label>
              <Input type="date" value={answers.birth_date} onChange={(e) => set('birth_date', e.target.value)} />
              {errors.birth_date && <p className="text-xs text-red-600 mt-1">{errors.birth_date}</p>}
            </div>
            <div {...invalid('phone')}>
              <Label className="text-xs">טלפון *</Label>
              <Input inputMode="tel" value={answers.phone} onChange={(e) => set('phone', e.target.value)} />
              {errors.phone && <p className="text-xs text-red-600 mt-1">{errors.phone}</p>}
            </div>
            <div {...invalid('email')}>
              <Label className="text-xs">אימייל *</Label>
              <Input inputMode="email" value={answers.email} onChange={(e) => set('email', e.target.value)} />
              {errors.email && <p className="text-xs text-red-600 mt-1">{errors.email}</p>}
            </div>
            <div>
              <Label className="text-xs">כתובת</Label>
              <Input value={answers.address} onChange={(e) => set('address', e.target.value)} />
            </div>
          </div>
        </Card>

        {/* Health questions */}
        <Card className="p-5 space-y-3">
          <h2 className="font-semibold">שאלון בריאות</h2>
          <p className="text-xs text-muted-foreground">האם קיים או היה בעברך אחד מהמצבים הבאים?</p>
          <div className="divide-y">
            {HEALTH_QUESTIONS.map((q) => (
              <div
                key={q.key}
                className="py-3 flex items-center justify-between gap-4"
                {...invalid(`questions.${q.key}`)}
              >
                <span className="text-sm flex-1">{q.label}</span>
                <div className="flex gap-2 shrink-0">
                  {(['yes', 'no'] as YesNo[]).map((v) => (
                    <button
                      key={v}
                      type="button"
                      onClick={() => set('questions', { ...answers.questions, [q.key]: v })}
                      className={`px-4 py-1.5 rounded-full text-sm border transition ${
                        answers.questions[q.key] === v
                          ? 'bg-neutral-900 text-white border-neutral-900'
                          : 'bg-white hover:bg-neutral-100'
                      } ${errors[`questions.${q.key}`] ? 'border-red-300' : ''}`}
                    >
                      {YES_NO_LABEL[v]}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Card>

        {/* Free text */}
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">מידע נוסף</h2>
          <div>
            <Label className="text-xs">תרופות קבועות</Label>
            <Textarea rows={2} value={answers.medications} onChange={(e) => set('medications', e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">מצב העור / רגישויות בעור</Label>
            <Textarea
              rows={2}
              value={answers.skin_condition}
              onChange={(e) => set('skin_condition', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">אלרגיות</Label>
            <Textarea rows={2} value={answers.allergies} onChange={(e) => set('allergies', e.target.value)} />
          </div>
          <div>
            <Label className="text-xs">טיפולים קודמים באזור</Label>
            <Textarea
              rows={2}
              value={answers.previous_treatments}
              onChange={(e) => set('previous_treatments', e.target.value)}
            />
          </div>
          <div>
            <Label className="text-xs">הערות</Label>
            <Textarea rows={2} value={answers.notes} onChange={(e) => set('notes', e.target.value)} />
          </div>
        </Card>

        {/* Treatment types */}
        <Card className="p-5 space-y-3" {...invalid('treatment_types')}>
          <h2 className="font-semibold">סוג הטיפול *</h2>
          <div className="flex flex-wrap gap-2">
            {TREATMENT_TYPES.map((t) => {
              const active = answers.treatment_types.includes(t);
              return (
                <button
                  key={t}
                  type="button"
                  onClick={() =>
                    set(
                      'treatment_types',
                      active
                        ? answers.treatment_types.filter((v) => v !== t)
                        : [...answers.treatment_types, t],
                    )
                  }
                  className={`px-4 py-2 rounded-full text-sm border transition ${
                    active ? 'bg-neutral-900 text-white border-neutral-900' : 'bg-white hover:bg-neutral-100'
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          {errors.treatment_types && <p className="text-xs text-red-600">{errors.treatment_types}</p>}
        </Card>

        {/* Consents */}
        <Card className="p-5 space-y-4">
          <h2 className="font-semibold">הצהרה והסכמה</h2>
          <div className="space-y-3">
            {CONSENT_ITEMS.map((c) => (
              <label key={c.key} className="flex gap-3 items-start" {...invalid(`consents.${c.key}`)}>
                <Checkbox
                  checked={!!answers.consents[c.key]}
                  onCheckedChange={(v) => set('consents', { ...answers.consents, [c.key]: v === true })}
                  className="mt-0.5"
                />
                <span className={`text-sm leading-relaxed ${errors[`consents.${c.key}`] ? 'text-red-600' : ''}`}>
                  {c.label}
                </span>
              </label>
            ))}
          </div>
          <div className="pt-2 space-y-2" {...invalid('photo_consent')}>
            <Label className="text-xs">אישור צילום</Label>
            <div className="flex flex-col gap-2">
              {(['approved', 'declined'] as PhotoConsent[]).map((v) => (
                <button
                  key={v}
                  type="button"
                  onClick={() => set('photo_consent', v)}
                  className={`text-start px-4 py-2 rounded-md text-sm border transition ${
                    answers.photo_consent === v
                      ? 'bg-neutral-900 text-white border-neutral-900'
                      : 'bg-white hover:bg-neutral-100'
                  }`}
                >
                  {PHOTO_CONSENT_LABEL[v]}
                </button>
              ))}
            </div>
            {errors.photo_consent && <p className="text-xs text-red-600">{errors.photo_consent}</p>}
          </div>
        </Card>

        {/* Signature */}
        <Card className="p-5 space-y-3" {...invalid('signature')}>
          <div className="flex items-center justify-between">
            <h2 className="font-semibold">חתימה *</h2>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => sigRef.current?.clear()}
            >
              <Eraser className="h-4 w-4 me-1" /> ניקוי
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">נא לחתום באצבע או בעכבר בתוך המסגרת.</p>
          <div
            className={`rounded-lg border-2 bg-white ${
              errors.signature ? 'border-red-300' : 'border-dashed border-neutral-300'
            }`}
          >
            <SignatureCanvas
              ref={(r) => (sigRef.current = r)}
              penColor="#1c1917"
              canvasProps={{ className: 'w-full h-40 touch-none rounded-lg' }}
            />
          </div>
          {errors.signature && <p className="text-xs text-red-600">{errors.signature}</p>}
          <p className="text-xs text-muted-foreground">תאריך: {todayLabel}</p>
        </Card>

        <Button className="w-full h-12 text-base" disabled={submitting} onClick={submit}>
          {submitting ? <Loader2 className="h-5 w-5 animate-spin me-2" /> : null}
          שליחת ההצהרה
        </Button>
        <p className="text-[11px] text-center text-muted-foreground pb-6">
          עותק חתום בפורמט PDF יישלח אליך ולקליניקה במייל.
        </p>
      </div>

      {/* Print template used to build the PDF (hidden on screen). */}
      <div className="fixed -left-[9999px] top-0" aria-hidden>
        <div
          ref={printRef}
          dir="rtl"
          style={{
            display: 'none',
            width: 794,
            background: '#ffffff',
            color: '#1c1917',
            fontFamily: PDF_FONT,
            fontSize: 12,
            lineHeight: 1.55,
          }}
        >
          <div
            data-pdf-page
            style={{
              width: 794,
              minHeight: 1123,
              padding: '38px 44px',
              boxSizing: 'border-box',
              background: '#ffffff',
            }}
          >
            {/* Header */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: '2px solid #1c1917',
                paddingBottom: 12,
              }}
            >
              <div>
                <div style={{ fontSize: 18, fontWeight: 700 }}>הצהרת בריאות והסכמה לטיפול</div>
                <div style={{ fontSize: 10, color: '#78716c', marginTop: 3 }}>
                  RIHAM · קליניקה אסתטית וטבעית · תאריך: {todayLabel}
                </div>
              </div>
              <img src={LOGO_SRC} alt="RIHAM" style={{ height: 46, width: 'auto' }} />
            </div>

            {/* Personal details */}
            <div style={sectionTitle}>פרטים אישיים</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {[
                ['שם מלא', answers.full_name],
                ['מספר זהות', answers.id_number],
                ['תאריך לידה', formatDate(answers.birth_date)],
                ['טלפון', answers.phone],
                ['אימייל', answers.email],
                ['כתובת', answers.address || '—'],
              ].map(([label, value]) => (
                <div key={label as string} style={{ width: '50%', padding: '5px 0' }}>
                  <span style={{ color: '#78716c' }}>{label}: </span>
                  <strong>{value || '—'}</strong>
                </div>
              ))}
            </div>

            {/* Questionnaire — two columns */}
            <div style={sectionTitle}>שאלון בריאות</div>
            <div style={{ display: 'flex', gap: 24 }}>
              {questionColumns.map((col, idx) => (
                <div key={idx} style={{ flex: 1 }}>
                  {col.map((q) => (
                    <div
                      key={q.key}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 8,
                        borderBottom: '1px solid #f1efed',
                        padding: '6px 0',
                      }}
                    >
                      <span>{q.label}</span>
                      <span
                        style={{
                          flexShrink: 0,
                          fontSize: 10,
                          fontWeight: 700,
                          border: '1px solid #d6d3d1',
                          borderRadius: 999,
                          padding: '1px 9px',
                          background: '#faf8f6',
                        }}
                      >
                        {answers.questions[q.key] ? YES_NO_LABEL[answers.questions[q.key]] : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ))}
            </div>

            {/* Extra info — two columns */}
            <div style={sectionTitle}>מידע נוסף</div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {[
                ['תרופות קבועות', answers.medications],
                ['מצב העור', answers.skin_condition],
                ['אלרגיות', answers.allergies],
                ['טיפולים קודמים', answers.previous_treatments],
              ].map(([label, value]) => (
                <div key={label as string} style={{ width: '50%', padding: '3px 12px 3px 0', boxSizing: 'border-box' }}>
                  <span style={{ color: '#78716c' }}>{label}: </span>
                  {value || '—'}
                </div>
              ))}
              <div style={{ width: '100%', padding: '3px 0' }}>
                <span style={{ color: '#78716c' }}>הערות: </span>
                {answers.notes || '—'}
              </div>
            </div>

            {/* Treatment types */}
            <div style={sectionTitle}>סוג הטיפול</div>
            <div>{answers.treatment_types.join(' · ') || '—'}</div>

            {/* Consents */}
            <div style={sectionTitle}>הצהרה והסכמה</div>
            <div>
              {CONSENT_ITEMS.map((c) => (
                <div key={c.key} style={{ display: 'flex', gap: 6, padding: '2px 0' }}>
                  <span style={{ color: '#4d7c0f', fontWeight: 700 }}>✓</span>
                  <span>{c.label}</span>
                </div>
              ))}
              <div style={{ marginTop: 5 }}>
                <span style={{ color: '#78716c' }}>אישור צילום: </span>
                <strong>
                  {answers.photo_consent ? PHOTO_CONSENT_LABEL[answers.photo_consent as PhotoConsent] : '—'}
                </strong>
              </div>
            </div>

            {/* Signature */}
            <div
              style={{
                marginTop: 18,
                border: '1px solid #e7e5e4',
                borderRadius: 8,
                padding: 14,
                background: '#fdfcfb',
                display: 'flex',
                alignItems: 'flex-end',
                justifyContent: 'space-between',
                gap: 20,
              }}
            >
              <div>
                <div style={{ fontSize: 11, color: '#78716c', marginBottom: 4 }}>חתימת המטופלת</div>
                {signatureData && (
                  <img src={signatureData} alt="חתימה" style={{ width: 230, height: 'auto', display: 'block' }} />
                )}
                <div style={{ borderTop: '1px solid #1c1917', width: 230, marginTop: 2 }} />
              </div>
              <div style={{ fontSize: 10, color: '#78716c', textAlign: 'left' }}>
                <div>נחתם דיגיטלית</div>
                <div>{todayLabel}</div>
              </div>
            </div>

            <div style={{ marginTop: 12, fontSize: 9, color: '#a8a29e', textAlign: 'center' }}>
              RIHAM Clinic · מסמך זה נחתם דיגיטלית ונשמר בקליניקה בהתאם למדיניות הפרטיות
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function formatDate(value: string): string {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? value : d.toLocaleDateString('he-IL');
}

const sectionTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 700,
  margin: '20px 0 7px',
  paddingBottom: 3,
  borderBottom: '1px solid #e7e5e4',
  color: '#1c1917',
};
